import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLabAccess } from "../../contexts/LabAccessContext";
import "./Dashboard.css";

const Dashboard = ({ user }) => {
    const navigate = useNavigate();
    const { openLab } = useLabAccess();
    const [labs, setLabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelingLabId, setCancelingLabId] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            setErrorMessage("");
            const response = await fetch("/api/dashboard", { method: "GET", credentials: "include", headers: { Accept: "application/json" } });
            if (response.status === 401) { navigate("/rect/login"); return; }
            if (!response.ok) throw new Error("Failed to load dashboard.");
            const data = await response.json();
            console.log(data)
            setLabs(Array.isArray(data.labs) ? data.labs : []);
        } catch (error) {
            console.error("Dashboard error:", error);
            setErrorMessage(error.message || "Unable to load your dashboard.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribeLab = (labId) => {
        navigate(`/rect/app/labs/pricing/${labId}`);
    };

    const handleCancelSubscription = async (labId) => {
        const confirmed = window.confirm("Are you sure you want to cancel this subscription?\n\nYou will continue to have access to the laboratory until the end of your current billing period.");
        if (!confirmed) return;
        try {
            setCancelingLabId(labId);
            setErrorMessage("");
            const response = await fetch("/api/billing/cancel-subscription", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ labId }) });
            if (response.status === 401) { navigate("/rect/login"); return; }
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Unable to cancel subscription.");
            alert(data.message || "Your subscription has been canceled.");
            await loadDashboard();
        } catch (error) {
            console.error("Cancel subscription error:", error);
            setErrorMessage(error.message || "Unable to cancel subscription.");
        } finally {
            setCancelingLabId(null);
        }
    };

    const navigateTo = (path) => {
        navigate(path);
    };

    if (loading) {
        return <div className="dashboard-loading"><div className="spinner-border text-primary" /><h5>Loading Dashboard...</h5></div>;
    }

    const robotLabs = labs.filter((lab) => lab.slug !== "vscode");
    const isAdmin = user?.role === "admin";

    return (
        <div className="dashboard-wrapper">
            <aside className="dashboard-sidebar">
                <div className="sidebar-brand">
                    <div className="brand-logo">S</div>
                    <div><div className="brand-name">STEMX365</div><div className="brand-subtitle">Space Robotics</div></div>
                </div>

                <nav className="sidebar-nav">
                    <button className="sidebar-item active" onClick={() => navigateTo("/dashboard")}><span>▦</span>Dashboard</button>
                    <button className="sidebar-item" onClick={() => navigateTo("/labs")}><span>🤖</span>My Labs</button>
                    <button className="sidebar-item" onClick={() => navigateTo("/courses")}><span>📚</span>Courses</button>
                    <button className="sidebar-item" onClick={() => navigateTo("/competitions")}><span>🏆</span>Competitions</button>
                    <button className="sidebar-item" onClick={() => navigateTo("/certificates")}><span>🎓</span>Certificates</button>
                    <button className="sidebar-item" onClick={() => navigateTo("/profile")}><span>👤</span>Profile</button>
                </nav>

                <div className="sidebar-footer">
                    <div className="help-box">
                        <strong>Need help?</strong>
                        <p>Check the documentation or contact your instructor.</p>
                        <button onClick={() => navigateTo("/support")}>Get Support →</button>
                    </div>
                </div>
            </aside>

            <main className="dashboard-main">
                <header className="dashboard-header">
                    <div>
                        <div className="dashboard-eyebrow">{isAdmin ? "ADMIN DASHBOARD" : "STUDENT DASHBOARD"}</div>
                        <h1>Welcome back, {user?.first_name || "Student"}!</h1>
                        <p>{isAdmin ? "Manage games, simulation software, and space robotics laboratories." : "Explore your space robotics laboratories and continue building."}</p>
                    </div>
                    <div className="user-profile">
                        <div className="user-avatar">{(user?.first_name?.[0] || "S").toUpperCase()}</div>
                        <div><strong>{user?.first_name || "Student"} {user?.last_name || ""}</strong><small>{user?.subscription_status || "Free"} Account</small></div>
                    </div>
                </header>

                {errorMessage && (
                    <div className="dashboard-error">
                        <strong>Unable to load dashboard</strong>
                        <p>{errorMessage}</p>
                        <button onClick={loadDashboard}>Try Again</button>
                    </div>
                )}

                {isAdmin && (
                    <section className="admin-tools-section">
                        <div className="section-header">
                            <div>
                                <h2>Administration</h2>
                                <p>Manage game projects and space robotics simulation software.</p>
                            </div>
                        </div>

                        <div className="admin-tools-grid">
                            <button className="admin-tool-card" onClick={() => navigateTo("/rect/admin/game-projects")}>
                                <div className="admin-tool-icon">🎮</div>
                                <div className="admin-tool-content">
                                    <h3>Game Projects</h3>
                                    <p>Select a game project and edit, build, and manage its source code.</p>
                                </div>
                                <span className="admin-tool-arrow">→</span>
                            </button>

                            <button className="admin-tool-card" onClick={() => navigateTo("/rect/admin/simulation")}>
                                <div className="admin-tool-icon">🛰️</div>
                                <div className="admin-tool-content">
                                    <h3>Simulation Software</h3>
                                    <p>Edit and build Astrobee or Int-Ball simulation software.</p>
                                </div>
                                <span className="admin-tool-arrow">→</span>
                            </button>
                        </div>
                    </section>
                )}

                <section className="labs-section">
                    <div className="section-header">
                        <div>
                            <h2>Space Robotics Labs</h2>
                            <p>Choose a lab to start programming and simulation.</p>
                        </div>
                        <button className="view-all-btn" onClick={() => navigateTo("/labs")}>View All →</button>
                    </div>

                    <div className="labs-grid">
                        {robotLabs.map((lab) => (
                            <LabCard key={lab.id} lab={lab} navigateTo={navigateTo} onSubscribe={() => handleSubscribeLab(lab.id)} onCancel={() => handleCancelSubscription(lab.id)} canceling={cancelingLabId === lab.id} />
                        ))}
                    </div>

                    {robotLabs.length === 0 && (
                        <div className="empty-state">
                            <div>🤖</div>
                            <h3>No labs available</h3>
                            <p>Your available laboratories will appear here.</p>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

const LabCard = ({ lab, navigateTo, onSubscribe, onCancel, canceling }) => {
    const subscribed = Boolean(lab.subscribed);

    return (
        <article className={`lab-card ${subscribed ? "active" : ""}`}>
            <div className="lab-card-image">
                {lab.image_url ? <img src={lab.image_url} alt={lab.name} className="lab-image" /> : <div className="lab-image-placeholder">🤖</div>}
                <span className={subscribed ? "lab-badge active" : "lab-badge"}>{subscribed ? "ACTIVE" : "SUBSCRIPTION REQUIRED"}</span>
            </div>

            <div className="lab-card-content">
                <h3>{lab.name}</h3>
                <p>{lab.description || "Explore this robotics laboratory and build your next space robotics project."}</p>
                <div className="lab-meta"><span>💻 Online IDE</span><span>🛰️ Simulation</span><span>ROS</span></div>
            </div>

            <div className="lab-card-footer">
                {subscribed ? (
                    <>
                        <button className="lab-open-btn" onClick={() => navigateTo(`/rect/admin/game-projects/`+lab.id)}>Open Lab <span>→</span></button>
                        <button className="lab-cancel-btn" onClick={() => onCancel(lab.id)} disabled={canceling}>{canceling ? "Canceling..." : "Cancel Subscription"}</button>
                    </>
                ) : (
                    <button className="lab-subscribe-btn" onClick={onSubscribe}>Subscribe <span>→</span></button>
                )}
            </div>
        </article>
    );
};

export default Dashboard;